/**
 * LiveTestStatus - Real-time test monitoring dashboard for lukhas.team
 *
 * Features:
 * - Live test status updates via polling/SSE
 * - Test suite overview with pass/fail/skip counts
 * - Coverage metrics visualization
 * - Recent test run history
 * - Drill-down to individual test results
 */

import React, { useState, useEffect, useCallback } from 'react'
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Activity
} from 'lucide-react'

interface TestResult {
  id: string
  name: string
  suite: string
  status: 'passed' | 'failed' | 'skipped' | 'running'
  duration: number
  errorMessage?: string
  timestamp: number
}

interface TestSuite {
  name: string
  total: number
  passed: number
  failed: number
  skipped: number
  running: number
  coverage: number
  lastRun: number
  tests: TestResult[]
}

interface TestSummary {
  total: number
  passed: number
  failed: number
  skipped: number
  running: number
  coverage: number
  lastUpdated: number
  suites: TestSuite[]
}

interface LiveTestStatusProps {
  className?: string
  refreshInterval?: number
}

export default function LiveTestStatus({ className = '', refreshInterval = 10000 }: LiveTestStatusProps) {
  const [summary, setSummary] = useState<TestSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [expandedSuites, setExpandedSuites] = useState<Set<string>>(new Set())
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [lastFetch, setLastFetch] = useState<number>(Date.now())

  // Mock data for demo mode
  const generateMockData = useCallback((): TestSummary => {
    const suites: TestSuite[] = [
      {
        name: 'core/consciousness',
        total: 45,
        passed: 43,
        failed: 1,
        skipped: 1,
        running: 0,
        coverage: 89.2,
        lastRun: Date.now() - 120000,
        tests: [
          { id: '1', name: 'should initialize consciousness state', suite: 'core/consciousness', status: 'passed', duration: 234, timestamp: Date.now() - 120000 },
          { id: '2', name: 'should handle memory persistence', suite: 'core/consciousness', status: 'passed', duration: 567, timestamp: Date.now() - 119000 },
          { id: '3', name: 'should validate guardian protocols', suite: 'core/consciousness', status: 'failed', duration: 123, errorMessage: 'Expected guardian state to be ACTIVE', timestamp: Date.now() - 118000 },
        ]
      },
      {
        name: 'api/gateway',
        total: 32,
        passed: 32,
        failed: 0,
        skipped: 0,
        running: 0,
        coverage: 94.5,
        lastRun: Date.now() - 180000,
        tests: [
          { id: '4', name: 'should authenticate requests', suite: 'api/gateway', status: 'passed', duration: 89, timestamp: Date.now() - 180000 },
          { id: '5', name: 'should rate limit properly', suite: 'api/gateway', status: 'passed', duration: 156, timestamp: Date.now() - 179000 },
        ]
      },
      {
        name: 'auth/lambda-id',
        total: 28,
        passed: 27,
        failed: 1,
        skipped: 0,
        running: 0,
        coverage: 91.3,
        lastRun: Date.now() - 60000,
        tests: [
          { id: '6', name: 'should generate valid ΛiD tokens', suite: 'auth/lambda-id', status: 'passed', duration: 345, timestamp: Date.now() - 60000 },
          { id: '7', name: 'should handle passphrase validation', suite: 'auth/lambda-id', status: 'failed', duration: 234, errorMessage: 'Passphrase entropy below threshold', timestamp: Date.now() - 59000 },
        ]
      },
      {
        name: 'guardian/protocols',
        total: 22,
        passed: 20,
        failed: 0,
        skipped: 2,
        running: 0,
        coverage: 86.7,
        lastRun: Date.now() - 300000,
        tests: [
          { id: '8', name: 'should enforce access controls', suite: 'guardian/protocols', status: 'passed', duration: 123, timestamp: Date.now() - 300000 },
          { id: '9', name: 'should audit sensitive operations', suite: 'guardian/protocols', status: 'skipped', duration: 0, timestamp: Date.now() - 299000 },
        ]
      }
    ]

    const total = suites.reduce((acc, s) => acc + s.total, 0)
    const passed = suites.reduce((acc, s) => acc + s.passed, 0)
    const failed = suites.reduce((acc, s) => acc + s.failed, 0)
    const skipped = suites.reduce((acc, s) => acc + s.skipped, 0)
    const running = suites.reduce((acc, s) => acc + s.running, 0)
    const coverage = suites.reduce((acc, s) => acc + s.coverage, 0) / suites.length

    return {
      total,
      passed,
      failed,
      skipped,
      running,
      coverage,
      lastUpdated: Date.now(),
      suites
    }
  }, [])

  const fetchTestStatus = useCallback(async () => {
    setIsRefreshing(true)
    try {
      // In production, this would fetch from the test runner API
      // const response = await fetch('/api/tests/status')
      // const data = await response.json()

      // For demo, use mock data
      await new Promise(resolve => setTimeout(resolve, 500))
      setSummary(generateMockData())
      setError(null)
      setLastFetch(Date.now())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch test status')
    } finally {
      setIsLoading(false)
      setIsRefreshing(false)
    }
  }, [generateMockData])

  // Initial fetch and polling
  useEffect(() => {
    fetchTestStatus()
    const interval = setInterval(fetchTestStatus, refreshInterval)
    return () => clearInterval(interval)
  }, [fetchTestStatus, refreshInterval])

  const toggleSuite = (suiteName: string) => {
    setExpandedSuites(prev => {
      const next = new Set(prev)
      if (next.has(suiteName)) {
        next.delete(suiteName)
      } else {
        next.add(suiteName)
      }
      return next
    })
  }

  const formatDuration = (ms: number) => {
    if (ms < 1000) return `${ms}ms`
    return `${(ms / 1000).toFixed(2)}s`
  }

  const formatTimeAgo = (timestamp: number) => {
    const seconds = Math.floor((Date.now() - timestamp) / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    return `${hours}h ago`
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'passed':
        return <CheckCircle className="w-4 h-4 text-emerald-400" />
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-400" />
      case 'skipped':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-400 animate-spin" />
      default:
        return <Clock className="w-4 h-4 text-gray-400" />
    }
  }

  if (isLoading) {
    return (
      <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 p-6 ${className}`}>
        <div className="flex items-center justify-center space-x-3">
          <RefreshCw className="w-5 h-5 text-lambda-gold animate-spin" />
          <span className="text-gray-400">Loading test status...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-red-500/30 p-6 ${className}`}>
        <div className="flex items-center space-x-3 text-red-400">
          <XCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!summary) return null

  const passRate = ((summary.passed / summary.total) * 100).toFixed(1)

  return (
    <div className={`bg-gray-900/80 backdrop-blur-sm rounded-xl border border-gray-700/50 overflow-hidden ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700/50 bg-gray-800/40">
        <div className="flex items-center space-x-3">
          <Activity className="w-5 h-5 text-lambda-gold" />
          <h3 className="text-lg font-medium text-white">Live Test Status</h3>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-xs text-gray-500">
            Updated {formatTimeAgo(lastFetch)}
          </span>
          <button
            onClick={fetchTestStatus}
            disabled={isRefreshing}
            className="p-1.5 rounded-lg bg-gray-700/50 hover:bg-gray-700 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-gray-400 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-5 border-b border-gray-700/50">
        <div className="text-center">
          <div className="text-2xl font-bold text-white">{summary.total}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Total</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-emerald-400">{summary.passed}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Passed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-red-400">{summary.failed}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Failed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-400">{summary.skipped}</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Skipped</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-lambda-gold">{summary.coverage.toFixed(1)}%</div>
          <div className="text-xs text-gray-500 uppercase tracking-wide">Coverage</div>
        </div>
      </div>

      {/* Pass Rate Bar */}
      <div className="px-5 py-3 border-b border-gray-700/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm text-gray-400">Pass Rate</span>
          <span className="text-sm font-medium text-white">{passRate}%</span>
        </div>
        <div className="h-2 bg-gray-700/50 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 transition-all duration-500"
            style={{ width: `${passRate}%` }}
          />
        </div>
      </div>

      {/* Test Suites */}
      <div className="divide-y divide-gray-700/30 max-h-[400px] overflow-y-auto">
        {summary.suites.map((suite) => (
          <div key={suite.name}>
            <button
              onClick={() => toggleSuite(suite.name)}
              className="w-full flex items-center justify-between px-5 py-3 hover:bg-gray-800/30 transition-colors"
            >
              <div className="flex items-center space-x-3">
                {expandedSuites.has(suite.name) ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <span className="text-sm font-medium text-white">{suite.name}</span>
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-1">
                  <span className="text-xs text-emerald-400">{suite.passed}</span>
                  <span className="text-xs text-gray-600">/</span>
                  <span className="text-xs text-gray-400">{suite.total}</span>
                </div>
                {suite.failed > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-red-500/20 text-red-400 rounded-full">
                    {suite.failed} failed
                  </span>
                )}
                <span className="text-xs text-gray-500">{formatTimeAgo(suite.lastRun)}</span>
              </div>
            </button>

            {/* Expanded Test List */}
            {expandedSuites.has(suite.name) && (
              <div className="bg-gray-800/20 px-5 py-2">
                {suite.tests.map((test) => (
                  <div
                    key={test.id}
                    className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-700/20"
                  >
                    <div className="flex items-center space-x-3">
                      {getStatusIcon(test.status)}
                      <span className="text-sm text-gray-300">{test.name}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-xs text-gray-500">{formatDuration(test.duration)}</span>
                    </div>
                  </div>
                ))}
                {suite.tests.length < suite.total && (
                  <div className="text-xs text-gray-500 text-center py-2">
                    +{suite.total - suite.tests.length} more tests
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
